import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Stack,
  Snackbar,
  Tabs,
  Tab,
  Collapse,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { usePageContext } from "../../context/PageContext";
import { useAuthContext } from "../../context/AuthContext";
import { fetchData } from "../../api/client";
import { useEffectivePermissions, toSet, can } from "../../hooks/useEffectivePermissions";
import ChatbotCreatePanel from "../../components/ChatbotFlow/ChatbotCreatePanel";
import ChatbotFlowWorkspace from "../../components/ChatbotFlow/ChatbotFlowWorkspace";
import ChatbotFlowImportExport from "../../components/ChatbotFlow/ChatbotFlowImportExport";
import ChatbotFlowAuditDialog from "../../components/ChatbotFlow/ChatbotFlowAuditDialog";
import ChatbotFlowOptimizationDialog from "../../components/ChatbotFlow/ChatbotFlowOptimizationDialog";
import ChatbotSettingsDialog from "../../components/ChatbotFlow/ChatbotSettingsDialog";
import { useTranslation } from "react-i18next";

export default function ChatbotForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("settings");
  const { userData } = useAuthContext();
  const { setCurrentPage } = usePageContext();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isNew = !id;

  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(() => toSet(permData?.scopes), [permData?.scopes]);
  const canUpdate = can(scopeSet, "chatbot", "update");
  const canCreate = can(scopeSet, "chatbot", "create");

  const userId = id ? parseInt(id, 10) : null;
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [botName, setBotName] = useState("");
  const [nameDraft, setNameDraft] = useState("");
  const [editingBot, setEditingBot] = useState(false);
  const [viewMode, setViewMode] = useState("canvas");
  const [showHelp, setShowHelp] = useState(false);
  const [focusSlotRequest, setFocusSlotRequest] = useState(null);

  const handleNavigateToSlot = useCallback((slotId) => {
    if (!slotId) return;
    setFocusSlotRequest({ slotId: Number(slotId), ts: Date.now() });
  }, []);

  useEffect(() => {
    const slotParam = Number(searchParams.get("slot")) || 0;
    if (!slotParam || !userId) return;
    setFocusSlotRequest({ slotId: slotParam, ts: Date.now() });
    const next = new URLSearchParams(searchParams);
    next.delete("slot");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, userId]);

  const loadBotMeta = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetchData(`/users/${userId}`, "GET");
      const u = res?.data ?? res;
      setBotName(u?.nome ?? "");
    } catch {
      setBotName("");
    }
  }, [userId]);

  useEffect(() => {
    if (!isNew && userId) {
      loadBotMeta();
    }
  }, [isNew, userId, loadBotMeta]);

  useEffect(() => {
    setNameDraft(botName || "");
  }, [botName]);

  useEffect(() => {
    const title = isNew ? t("chatbotFormPage.newTitle") : botName || t("chatbotConfigPage.title");
    setCurrentPage({ title, showBackButton: false });
  }, [setCurrentPage, isNew, botName, t]);

  const nameDirty = useMemo(
    () => nameDraft.trim() !== (botName || "").trim(),
    [nameDraft, botName]
  );

  const handleSaveBotName = async () => {
    if (!userId) return;
    if (!nameDraft.trim()) {
      setError(t("chatbotConfigPage.nameRequired"));
      return;
    }
    setEditingBot(true);
    try {
      setError(null);
      await fetchData(`/users/${userId}`, "PUT", {
        nome: nameDraft.trim(),
        id_usuario_ultima_alteracao: userData?.id ?? null,
      });
      setBotName(nameDraft.trim());
      queryClient.invalidateQueries(["chatbots"]);
      setSnack({ open: true, severity: "success", message: t("chatbotFormPage.snackRenamed") });
    } catch (err) {
      const baseMsg =
        err?.message ||
        err?.error ||
        (Array.isArray(err?.message) && err.message[0]) ||
        t("chatbotConfigPage.errorEditUser");
      const detail =
        (typeof err?.data === "string" && err.data) ||
        (typeof err?.details === "string" && err.details) ||
        null;
      setError(detail ? `${baseMsg} (${detail})` : baseMsg);
    } finally {
      setEditingBot(false);
    }
  };

  if (isNew) {
    return (
      <Box sx={{ pt: 1, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/chatbots")}
          >
            {t("chatbotFormPage.backToList")}
          </Button>
        </Stack>
        <Typography variant="h4" component="h1" gutterBottom>
          {t("chatbotFormPage.newTitle")}
        </Typography>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <ChatbotCreatePanel
          canCreate={canCreate}
          userData={userData}
          onError={setError}
          onSnack={setSnack}
        />

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          message={snack.message}
        />
      </Box>
    );
  }

  if (!userId || Number.isNaN(userId)) {
    return (
      <Box sx={{ pt: 2 }}>
        <Alert severity="warning">{t("chatbotFormPage.invalidId")}</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/chatbots")}>
          {t("chatbotFormPage.backToList")}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: { xs: 2, sm: 3 }, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate("/chatbots")}
          sx={{ textTransform: "none" }}
        >
          {t("chatbotFormPage.backToList")}
        </Button>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ChatbotSettingsDialog
            userId={userId}
            disabled={!userId}
            canEdit={canUpdate}
            onError={(msg) => setError(msg)}
            onSuccess={(msg) => setSnack({ open: true, severity: "success", message: msg })}
          />
          {canUpdate && (
            <>
              <ChatbotFlowAuditDialog
                userId={userId}
                chatbotName={botName}
                disabled={!userId}
                onError={(msg) => setError(msg)}
                onSuccess={(msg) => setSnack({ open: true, severity: "success", message: msg })}
                onNavigateToSlot={handleNavigateToSlot}
              />
              <ChatbotFlowOptimizationDialog
                userId={userId}
                chatbotName={botName}
                disabled={!userId}
                onError={(msg) => setError(msg)}
                onSuccess={(msg) => setSnack({ open: true, severity: "success", message: msg })}
                onNavigateToSlot={handleNavigateToSlot}
              />
              <ChatbotFlowImportExport
                userId={userId}
                chatbotName={botName}
                disabled={!userId}
                onError={(msg) => setError(msg)}
                onSuccess={(msg) => setSnack({ open: true, severity: "success", message: msg })}
              />
            </>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<BarChartRoundedIcon />}
            onClick={() => navigate(`/chatbots/${userId}/analytics`)}
            sx={{ textTransform: "none" }}
          >
            {t("agentsListPage.analytics")}
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          mb: showHelp ? 1 : 2,
          justifyContent: "space-between",
        }}
      >
        {canUpdate ? (
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={1.5}
            alignItems={isMobile ? "stretch" : "flex-start"}
            sx={{ flex: { sm: "0 1 auto" }, minWidth: 0 }}
          >
            <TextField
              label={t("chatbotConfigPage.chatbotName")}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && nameDirty && !editingBot) {
                  e.preventDefault();
                  handleSaveBotName();
                }
              }}
              size="small"
              fullWidth={isMobile}
              sx={{ minWidth: { sm: 280 }, maxWidth: { sm: 480 } }}
              disabled={editingBot}
            />
            <Button
              variant="contained"
              size="small"
              disabled={!nameDirty || editingBot}
              onClick={handleSaveBotName}
              sx={{ alignSelf: isMobile ? "stretch" : "auto", mt: { sm: "4px !important" } }}
            >
              {t("shared.save")}
            </Button>
          </Stack>
        ) : (
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {botName || t("chatbotConfigPage.heading")}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ ml: { sm: "auto" } }}
        >
          <Tabs
            value={viewMode}
            onChange={(_, v) => setViewMode(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 32,
              "& .MuiTab-root": { minHeight: 32, textTransform: "none", fontSize: 13, px: 1.5 },
            }}
          >
            <Tab label={t("chatbotConfigPage.tabCanvas")} value="canvas" />
            <Tab label={t("chatbotConfigPage.tabTree")} value="tree" />
          </Tabs>
          <Button
            size="small"
            endIcon={showHelp ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setShowHelp((v) => !v)}
            sx={{ textTransform: "none" }}
          >
            {t("chatbotConfigPage.learnMore")}
          </Button>
        </Stack>
      </Box>

      <Collapse in={showHelp}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {t("chatbotConfigPage.legendBody")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.5 }}>
            {t("chatbotConfigPage.helpCanvas")}
          </Typography>
        </Box>
      </Collapse>

      <ChatbotFlowWorkspace
        userId={userId}
        viewMode={viewMode}
        error={error}
        setError={setError}
        focusSlotRequest={focusSlotRequest}
        onNavigateToSlot={handleNavigateToSlot}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
}
